import { BaseError } from './base-error';
import { ErrorCategory, ErrorCode, ErrorSeverity } from './types';
import type { ErrorContext } from './types';

export interface AuthErrorOptions {
  code: ErrorCode;
  message: string;
  context?: ErrorContext;
  cause?: Error;
  severity?: ErrorSeverity;
}

/**
 * Authentication and authorization related errors
 */
export class AuthError extends BaseError {
  constructor(options: AuthErrorOptions) {
    super({
      ...options,
      category: ErrorCategory.Authentication,
      severity: options.severity || ErrorSeverity.Warning,
    });

    Object.setPrototypeOf(this, AuthError.prototype);
  }

  /**
   * Create an invalid token error
   */
  static invalidToken(context?: ErrorContext, cause?: Error): AuthError {
    return new AuthError({
      code: ErrorCode.AUTH_INVALID_TOKEN,
      message: 'Invalid authentication token provided',
      context,
      cause,
    });
  }

  /**
   * Create an expired token error
   */
  static expiredToken(context?: ErrorContext, cause?: Error): AuthError {
    return new AuthError({
      code: ErrorCode.AUTH_EXPIRED_TOKEN,
      message: 'Authentication token has expired',
      context,
      cause,
    });
  }

  /**
   * Create a missing token error
   */
  static missingToken(context?: ErrorContext, cause?: Error): AuthError {
    return new AuthError({
      code: ErrorCode.AUTH_MISSING_TOKEN,
      message: 'No authorization token included in request',
      context,
      cause,
    });
  }

  /**
   * Create an invalid credentials error
   */
  static invalidCredentials(context?: ErrorContext, cause?: Error): AuthError {
    return new AuthError({
      code: ErrorCode.AUTH_INVALID_CREDENTIALS,
      message: 'Invalid username or password',
      context,
      cause,
    });
  }

  /**
   * Create a user not found error
   */
  static userNotFound(context?: ErrorContext, cause?: Error): AuthError {
    return new AuthError({
      code: ErrorCode.AUTH_USER_NOT_FOUND,
      message: 'User not found',
      context,
      cause,
    });
  }

  /**
   * Create a user suspended error
   */
  static userSuspended(context?: ErrorContext, cause?: Error): AuthError {
    return new AuthError({
      code: ErrorCode.AUTH_USER_SUSPENDED,
      message: 'User account has been suspended',
      context,
      cause,
    });
  }

  /**
   * Create an insufficient permissions error
   */
  static insufficientPermissions(
    context?: ErrorContext,
    cause?: Error
  ): AuthError {
    return new AuthError({
      code: ErrorCode.AUTH_INSUFFICIENT_PERMISSIONS,
      message: 'Insufficient permissions to access this resource',
      context,
      cause,
      severity: ErrorSeverity.Warning,
    });
  }

  /**
   * Check if error message indicates authentication issue
   * Used for migration from string-based detection
   */
  static isAuthRelated(message: string): boolean {
    const authKeywords = [
      'jwt',
      'unauthorized',
      'no authorization included',
      'user not found',
      'invalid token',
      'expired token',
      'access denied',
      'forbidden',
      'authentication',
      'auth',
      'login',
      'credential',
    ];

    const lowerMessage = message.toLowerCase();
    return authKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  /**
   * Create auth error from string message (migration helper)
   */
  static fromMessage(
    message: string,
    context?: ErrorContext,
    cause?: Error
  ): AuthError {
    const lowerMessage = message.toLowerCase();

    if (
      lowerMessage.includes('jwt') ||
      lowerMessage.includes('invalid token')
    ) {
      return AuthError.invalidToken(context, cause);
    }

    if (lowerMessage.includes('expired')) {
      return AuthError.expiredToken(context, cause);
    }

    if (lowerMessage.includes('no authorization included')) {
      return AuthError.missingToken(context, cause);
    }

    if (lowerMessage.includes('user not found')) {
      return AuthError.userNotFound(context, cause);
    }

    if (
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('access denied')
    ) {
      return AuthError.insufficientPermissions(context, cause);
    }

    // Generic auth error
    return new AuthError({
      code: ErrorCode.AUTH_INVALID_TOKEN,
      message: message,
      context,
      cause,
    });
  }
}
